// The staff guards (Frontend §5.3), for TanStack Router `beforeLoad`. Rows 1–3 run on every navigation under
// `_authed`; row 4 (home) and row 5 (clinic id) are each app's. The server still decides every request.
import { isApiError } from '@pa/api'
import type { Me } from '@pa/api/me'
import type { QueryClient } from '@tanstack/react-query'
import { redirect } from '@tanstack/react-router'
import { signInHref } from '../auth/redirect'
import type { AuthStore } from '../auth/store'
import { activeMemberships, meQueryOptions } from '../me/me'

export type Site = 'admin' | 'clinic'

/** Throws the router redirect (TanStack's `redirect({ throw: true })`), typed as never. */
function goTo(href: string): never {
  redirect({ href, replace: true, throw: true })
  throw new Error('unreachable: redirect() throws')
}

export interface GuardContext {
  auth: AuthStore
  queryClient: QueryClient
}

/** Row 1: signed in, or → A-01 with `?redirect=` back here. */
export async function requireSignedIn(ctx: GuardContext, returnTo: string): Promise<void> {
  await ctx.auth.ready()
  if (ctx.auth.status !== 'signedIn') {
    goTo(signInHref({ redirect: returnTo }))
  }
}

/** `/v1/me` from the cache or the API. A 401 means the session is gone: → A-01 "Please sign in again". */
export async function loadMe(ctx: GuardContext, returnTo: string): Promise<Me> {
  try {
    return await ctx.queryClient.query(meQueryOptions())
  } catch (e) {
    if (isApiError(e) && e.code === 'unauthorized') {
      goTo(signInHref({ reason: 'session-expired', redirect: returnTo }))
    }
    throw e
  }
}

/** Row 3: may this user use this site at all? */
export function hasSiteAccess(me: Me, site: Site): boolean {
  if (site === 'admin') return me.platformRole === 'super_admin'
  // Clinic site: an active membership, or an impersonation (task 13). Super admins have no memberships, so without
  // an impersonation they get "no access" (D-40). All memberships inactive: see `clinicInactive`.
  return me.impersonation !== null || activeMemberships(me).length > 0
}

/** Clinic site, row 3: memberships, every one of them inactive, and no impersonation (P-10). */
export function clinicInactive(me: Me): boolean {
  return me.impersonation === null && me.memberships.length > 0 && activeMemberships(me).length === 0
}

/** Rows 1–3, in order. Returns `me` for the child routes' context. */
export async function guardAuthed(ctx: GuardContext, site: Site, returnTo: string): Promise<{ me: Me }> {
  await requireSignedIn(ctx, returnTo)
  const me = await loadMe(ctx, returnTo)
  if (me.mustChangePassword) goTo('/set-password') // row 2: only A-04
  if (site === 'clinic' && clinicInactive(me)) {
    // Row 3: every clinic of this user was deactivated → sign out, A-01 "This clinic's account is not active".
    try {
      await ctx.auth.signOutQuietly()
    } finally {
      ctx.queryClient.clear()
    }
    goTo(signInHref({ reason: 'clinic-inactive' }))
  }
  if (!hasSiteAccess(me, site)) goTo('/no-access') // row 3
  return { me }
}

/** A-04's own guard: signed in and flagged; otherwise home. */
export async function guardSetPassword(ctx: GuardContext): Promise<{ me: Me }> {
  await requireSignedIn(ctx, '/set-password')
  const me = await loadMe(ctx, '/set-password')
  if (!me.mustChangePassword) goTo('/')
  return { me }
}

/** The no-access page's guard: signed in (else A-01); with access after all → home. */
export async function guardNoAccess(ctx: GuardContext, site: Site): Promise<{ me: Me }> {
  await requireSignedIn(ctx, '/')
  const me = await loadMe(ctx, '/')
  if (me.mustChangePassword) goTo('/set-password')
  if (hasSiteAccess(me, site)) goTo('/')
  return { me }
}
